import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { calculate, waterForUnits, SYRINGES, DOSE_UNITS, DEFAULT_SYRINGE } from '../lib/peptide.js'
import { useAuth } from '../context/AuthContext.jsx'
import { useUserData } from '../lib/useUserData.js'
import { useToast } from '../context/ToastContext.jsx'
import SyringeVisual from './SyringeVisual.jsx'
import EmailCapture from './EmailCapture.jsx'

const PRESETS = [
  { name: 'BPC-157', vialMg: 5, waterMl: 2, dose: 250, doseUnit: 'mcg' },
  { name: 'TB-500', vialMg: 5, waterMl: 2, dose: 2, doseUnit: 'mg' },
  { name: 'GLP-1', vialMg: 5, waterMl: 2, dose: 250, doseUnit: 'mcg' },
  { name: 'CJC-1295', vialMg: 5, waterMl: 2.5, dose: 100, doseUnit: 'mcg' },
  { name: 'Ipamorelin', vialMg: 5, waterMl: 2, dose: 300, doseUnit: 'mcg' },
]

const MAX_EXTRAS = 3
const empty = { vialMg: '', waterMl: '', dose: '', doseUnit: 'mcg', syringe: DEFAULT_SYRINGE, targetUnits: '' }

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : NaN }
let _eid = 0
const nextId = () => ++_eid
const newExtra = (e = {}) => ({ id: nextId(), name: '', vialMg: '', waterMl: '', dose: '', doseUnit: 'mcg', ...e })

export default function CalculatorForm({ initial, embed = false }) {
  const [mode, setMode] = useState('units') // 'units' | 'water'
  const [form, setForm] = useState({ ...empty, ...initial })
  const [extras, setExtras] = useState(() => (initial?.extras || []).map(newExtra))
  const [touched, setTouched] = useState(false)
  const { user } = useAuth()
  const { saveCalc } = useUserData()
  const toast = useToast()

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setTouched(true) }
  const setVal = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setTouched(true) }

  // In reverse mode, derive the water from the target units, then run the normal math.
  const { result, computedWater } = useMemo(() => {
    if (mode === 'water') {
      const w = waterForUnits({ vialMg: form.vialMg, dose: form.dose, doseUnit: form.doseUnit, targetUnits: form.targetUnits, syringe: form.syringe })
      if (w == null) return { result: calculate({ ...form, waterMl: NaN }), computedWater: null }
      return { result: calculate({ ...form, waterMl: w }), computedWater: w }
    }
    return { result: calculate(form), computedWater: null }
  }, [mode, form])

  const displayWater = mode === 'water' ? computedWater : form.waterMl

  const applyPreset = (p) => {
    setForm((f) => ({ ...f, vialMg: p.vialMg, waterMl: p.waterMl, dose: p.dose, doseUnit: p.doseUnit, name: p.name }))
    setTouched(true)
  }

  const addExtra = () => setExtras((x) => (x.length < MAX_EXTRAS ? [...x, newExtra()] : x))
  const removeExtra = (id) => setExtras((x) => x.filter((e) => e.id !== id))
  const updateExtra = (id, k, v) => setExtras((x) => x.map((e) => (e.id === id ? { ...e, [k]: v } : e)))

  const extraResults = useMemo(
    () => extras.map((e) => ({ e, res: calculate({ ...e, syringe: form.syringe }) })),
    [extras, form.syringe]
  )
  const validExtras = extraResults.filter((x) => x.res.ok)
  const showExtras = mode === 'units'

  const err = (k) => (touched && result.errors?.[k]) || ''
  const targetErr = mode === 'water' && touched && !(num(form.targetUnits) > 0) ? 'Enter how many units you want each dose to be.' : ''
  const unitLabel = DOSE_UNITS.find((u) => u.id === form.doseUnit)?.label || 'mcg'
  const capacity = SYRINGES.find((s) => s.id === form.syringe)?.capacity ?? 100

  // Live mg↔mcg conversion shown under the dose field so mg/mcg mix-ups are obvious.
  const trim = (n) => String(+n.toFixed(4))
  const doseNum = num(form.dose)
  const doseHint = doseNum > 0
    ? (form.doseUnit === 'mg'
        ? `${trim(doseNum)} mg = ${(doseNum * 1000).toLocaleString()} mcg`
        : `${trim(doseNum)} mcg = ${trim(doseNum / 1000)} mg`)
    : ''

  const [showMath, setShowMath] = useState(false)
  const primaryName = form.name || PRESETS.find((p) => p.vialMg == form.vialMg)?.name || 'Peptide 1'

  const combinedItems = showExtras && result.ok
    ? [{ name: primaryName, units: result.units }, ...validExtras.map((x, i) => ({ name: x.e.name?.trim() || `Peptide ${i + 2}`, units: x.res.units }))]
    : []
  const totalUnits = Math.round(combinedItems.reduce((s, it) => s + it.units, 0) * 100) / 100
  const showCombined = showExtras && result.ok && validExtras.length > 0

  // Snapshot of the current inputs, handed to /register (or /login) so a new
  // visitor's calculation is waiting for them to save right after they sign up.
  const pendingCalc = {
    name: form.name, vialMg: form.vialMg, waterMl: displayWater, dose: form.dose,
    doseUnit: form.doseUnit, syringe: form.syringe,
    extras: showExtras ? extras.map(({ name, vialMg, waterMl, dose, doseUnit }) => ({ name, vialMg, waterMl, dose, doseUnit })) : [],
  }

  // Compact result payload emailed by "email me this calculation" (rendered server-side).
  const emailPayload = result.ok ? {
    name: primaryName,
    vialMg: form.vialMg, waterMl: displayWater, dose: form.dose, doseUnit: unitLabel,
    units: result.units, volumeMl: result.volumeMl,
    concentrationMgMl: result.concentrationMgMl, mcgPerUnit: result.mcgPerUnit,
    dosesPerVial: result.dosesPerVial, syringe: result.capacity,
  } : null

  const [saving, setSaving] = useState(false)
  const handleSave = async () => {
    const name = window.prompt('Name this calculation:', form.name || `${primaryName} ${form.dose}${unitLabel}`)
    if (name == null) return
    setSaving(true)
    const res = await saveCalc({
      name: name.trim() || 'Untitled',
      vialMg: form.vialMg, waterMl: displayWater, dose: form.dose, doseUnit: form.doseUnit, syringe: form.syringe,
      snapshot: {
        units: result.units, volumeMl: result.volumeMl, concentrationMgMl: result.concentrationMgMl,
        dosesPerVial: result.dosesPerVial,
        extras: showExtras ? extras.map(({ name, vialMg, waterMl, dose, doseUnit }) => ({ name, vialMg, waterMl, dose, doseUnit })) : [],
        totalUnits: showCombined ? totalUnits : result.units,
      },
    })
    setSaving(false)
    if (res?.error) return toast(res.error, 'red')
    toast('Calculation saved', 'green')
  }

  return (
    <div className="grid-calc">
      {/* ---------- inputs ---------- */}
      <div className="card card-pad">
        {/* mode toggle */}
        <div className="mode-toggle">
          <button type="button" className={mode === 'units' ? 'active' : ''} onClick={() => setMode('units')}>
            <span className="mt-title">I know my water</span>
            <span className="mt-sub">→ show units to draw</span>
          </button>
          <button type="button" className={mode === 'water' ? 'active' : ''} onClick={() => setMode('water')}>
            <span className="mt-title">Help me pick water</span>
            <span className="mt-sub">→ show water to add</span>
          </button>
        </div>

        <div className="preset-row">
          {PRESETS.map((p) => (
            <button key={p.name} className="preset-chip" onClick={() => applyPreset(p)} type="button">{p.name}</button>
          ))}
        </div>

        <div className="field">
          <label className="label">Peptide in vial <span className="hint">(total mg)</span></label>
          <div className="input-group">
            <input className="input" type="number" min="0" step="any" inputMode="decimal"
              placeholder="e.g. 5" value={form.vialMg} onChange={set('vialMg')} />
            <span className="addon">mg</span>
          </div>
          {err('vialMg') && <div className="form-error">{err('vialMg')}</div>}
        </div>

        <div className="field">
          <label className="label">Desired dose</label>
          <div className="row" style={{ gap: 10, alignItems: 'stretch' }}>
            <input className="input" type="number" min="0" step="any" inputMode="decimal"
              placeholder="e.g. 250" value={form.dose} onChange={set('dose')} style={{ flex: 1 }} />
            <div className="seg">
              {DOSE_UNITS.map((u) => (
                <button key={u.id} type="button" className={form.doseUnit === u.id ? 'active' : ''}
                  onClick={() => setVal('doseUnit', u.id)}>{u.label}</button>
              ))}
            </div>
          </div>
          {err('dose') && <div className="form-error">{err('dose')}</div>}
          {doseHint && <div className="dose-conv">↔ {doseHint}</div>}
        </div>

        {mode === 'units' ? (
          <div className="field">
            <label className="label">Bacteriostatic water <span className="hint">(added to vial)</span></label>
            <div className="input-group">
              <input className="input" type="number" min="0" step="any" inputMode="decimal"
                placeholder="e.g. 2" value={form.waterMl} onChange={set('waterMl')} />
              <span className="addon">mL</span>
            </div>
            {err('waterMl') && <div className="form-error">{err('waterMl')}</div>}
            <div className="field-help">
              How much water you add is your choice — it <strong>doesn’t change your dose</strong>, only how many
              units you draw. Common:
              <span className="water-chips">
                {[1, 2, 3].map((v) => (
                  <button key={v} type="button" className="water-chip" onClick={() => setVal('waterMl', v)}>{v} mL</button>
                ))}
              </span>
            </div>
          </div>
        ) : (
          <div className="field">
            <label className="label">Draw about <span className="hint">(units per dose)</span></label>
            <div className="input-group">
              <input className="input" type="number" min="0" step="any" inputMode="decimal"
                placeholder="e.g. 20" value={form.targetUnits} onChange={set('targetUnits')} />
              <span className="addon">units</span>
            </div>
            {targetErr && <div className="form-error">{targetErr}</div>}
            <div className="field-help">
              Pick an easy-to-read number of units you’d like to draw for each dose — we’ll tell you exactly how
              much water to add. (10–50 units is typical.)
            </div>
          </div>
        )}

        {/* additional stacked peptides (units mode only) */}
        {showExtras && extras.map((e, i) => (
          <div className="extra-pep" key={e.id}>
            <div className="between" style={{ marginBottom: 10 }}>
              <span className="extra-pep-label">Additional peptide {i + 1}</span>
              <button type="button" className="extra-remove" onClick={() => removeExtra(e.id)} aria-label="Remove">✕ Remove</button>
            </div>
            <div className="field" style={{ marginBottom: 10 }}>
              <label className="label sm">Name <span className="hint">(optional)</span></label>
              <input className="input" placeholder="e.g. TB-500" value={e.name} onChange={(ev) => updateExtra(e.id, 'name', ev.target.value)} />
            </div>
            <div className="extra-grid">
              <div className="xf">
                <label className="label sm">Vial</label>
                <div className="input-group"><input className="input" type="number" min="0" step="any" placeholder="5"
                  value={e.vialMg} onChange={(ev) => updateExtra(e.id, 'vialMg', ev.target.value)} /><span className="addon">mg</span></div>
              </div>
              <div className="xf">
                <label className="label sm">Water</label>
                <div className="input-group"><input className="input" type="number" min="0" step="any" placeholder="2"
                  value={e.waterMl} onChange={(ev) => updateExtra(e.id, 'waterMl', ev.target.value)} /><span className="addon">mL</span></div>
              </div>
              <div className="xf">
                <label className="label sm">Dose</label>
                <input className="input" type="number" min="0" step="any" placeholder="250"
                  value={e.dose} onChange={(ev) => updateExtra(e.id, 'dose', ev.target.value)} />
              </div>
              <div className="xf">
                <label className="label sm">Unit</label>
                <div className="seg">
                  {DOSE_UNITS.map((u) => (
                    <button key={u.id} type="button" className={e.doseUnit === u.id ? 'active' : ''}
                      onClick={() => updateExtra(e.id, 'doseUnit', u.id)}>{u.label}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}

        {showExtras && extras.length < MAX_EXTRAS && (
          <button type="button" className="btn btn-ghost btn-sm add-extra" onClick={addExtra}>
            ＋ Add another peptide (combine in one syringe)
          </button>
        )}

        <div className="field mb-0" style={{ marginTop: 16 }}>
          <label className="label">Syringe size <span className="hint">(U-100 insulin)</span></label>
          <div className="seg" style={{ width: '100%' }}>
            {SYRINGES.map((s) => (
              <button key={s.id} type="button" style={{ flex: 1 }}
                className={form.syringe === s.id ? 'active' : ''} onClick={() => setVal('syringe', s.id)}>
                <span style={{ fontWeight: 700 }}>{s.capacity}u</span>
                <span className="muted" style={{ fontSize: '.72rem', display: 'block' }}>{s.ml}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ---------- results ---------- */}
      <div className="card result-card">
        {result.ok ? (
          <div className="fade-in">
            <div className="result-hero">
              {mode === 'water' ? (
                <>
                  <div className="result-hero-label">Add this much water</div>
                  <div className="result-hero-value">{computedWater}<span className="unit"> mL</span></div>
                  <div className="result-hero-sub">→ then draw {result.units} units for each dose</div>
                </>
              ) : (
                <>
                  <div className="result-hero-label">Draw to {showCombined && '(peptide 1)'}</div>
                  <div className="result-hero-value">{result.units}<span className="unit"> units</span></div>
                  <div className="result-hero-sub">= {result.volumeMl} mL on a {result.syringeLabel} syringe</div>
                </>
              )}
            </div>

            <div className="syringe-box">
              <SyringeVisual units={result.units} capacity={result.capacity} />
            </div>

            <div className="result-grid">
              <Stat label="Concentration" value={result.concentrationMgMl} unit="mg/mL" />
              <Stat label="Per unit" value={result.mcgPerUnit} unit="mcg/u" />
              <Stat label="Doses per vial" value={result.dosesPerVial} unit={`× ${form.dose}${unitLabel}`} />
              <Stat label={mode === 'water' ? 'Water to add' : 'Volume'} value={mode === 'water' ? computedWater : result.volumeMl} unit="mL" />
            </div>

            <div className="directions">
              <div className="directions-title">📋 In plain English</div>
              <p>
                Add <strong>{displayWater} mL</strong> of bacteriostatic water to your{' '}
                <strong>{form.vialMg} mg</strong> vial. That gives you about{' '}
                <strong>{result.dosesPerVial} doses</strong> of <strong>{form.dose} {unitLabel}</strong>.
                For each dose, draw <strong>{result.units} units</strong> into a{' '}
                <strong>{result.capacity}-unit</strong> insulin syringe.
              </p>
            </div>

            <div className="math-toggle">
              <button type="button" className="math-btn" onClick={() => setShowMath((v) => !v)} aria-expanded={showMath}>
                {showMath ? '▾ Hide the math' : '▸ Show the math'}
              </button>
              {showMath && (
                <ol className="math-steps">
                  <li><span>Peptide in vial</span><code>{form.vialMg} mg × 1,000 = {(num(form.vialMg) * 1000).toLocaleString()} mcg</code></li>
                  <li><span>Concentration</span><code>{(num(form.vialMg) * 1000).toLocaleString()} mcg ÷ {displayWater} mL = {result.concentrationMcgMl.toLocaleString()} mcg/mL</code></li>
                  <li><span>Per unit</span><code>{result.concentrationMcgMl.toLocaleString()} mcg/mL ÷ 100 u/mL = {result.mcgPerUnit} mcg/unit</code></li>
                  <li><span>Units to draw</span><code>{result.doseMcg.toLocaleString()} mcg ÷ {result.mcgPerUnit} mcg/unit = {result.units} units</code></li>
                </ol>
              )}
            </div>

            {showCombined && (
              <div className="combined">
                <div className="combined-title">🧪 Combined draw — one syringe</div>
                {combinedItems.map((it, i) => (
                  <div className="combined-row" key={i}>
                    <span className="combined-name">{it.name}</span>
                    <span className="combined-units">{it.units} u</span>
                  </div>
                ))}
                <div className="combined-row combined-total">
                  <span>Total to draw</span>
                  <span>{totalUnits} units</span>
                </div>
                <div style={{ marginTop: 6 }}>
                  <SyringeVisual units={totalUnits} capacity={capacity} />
                </div>
                {totalUnits > capacity && (
                  <div className="warn" style={{ margin: '10px 0 0' }}>
                    ⚠️ The combined {totalUnits} units is more than a {capacity}-unit syringe holds. Use a larger syringe or split the injections.
                  </div>
                )}
              </div>
            )}

            {result.warnings.map((w, i) => (<div key={i} className="warn">⚠️ {w}</div>))}

            <div className="result-actions">
              {embed ? (
                <a className="btn btn-soft btn-block" href="https://calculatemypeptides.com/calculator" target="_blank" rel="noopener">
                  Save &amp; schedule on CalculateMyPeptides ↗
                </a>
              ) : user ? (
                <button className="btn btn-primary btn-block" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : '💾 Save calculation'}
                </button>
              ) : (
                <EmailCapture payload={emailPayload} pendingCalc={pendingCalc} />
              )}
            </div>
          </div>
        ) : (
          <div className="result-empty">
            <div className="syringe-box faded">
              <SyringeVisual units={0} capacity={capacity} />
            </div>
            <p className="muted center" style={{ maxWidth: 320, margin: '0 auto' }}>
              {mode === 'water'
                ? 'Enter your vial, dose, and how many units you want to draw — we’ll tell you how much water to add.'
                : 'Enter your vial amount, water, and dose to see exactly how many units to draw.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value, unit }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}{unit && <span className="stat-unit"> {unit}</span>}</div>
    </div>
  )
}
